/**
 * Tile previews, fetched for the tiles ON SCREEN and never in the way.
 *
 * TASK-042 §5b. Two rules, and the whole module exists to keep them:
 *
 * 1. **Only the visible tiles.** An IntersectionObserver -- one, shared, not
 *    one per tile -- says which tiles are in the viewport, and only those ids
 *    are asked for. A folder of ninety documents scrolled to the top costs one
 *    request for the dozen a person can see.
 *
 * 2. **A sketch fetch never blocks a tile appearing.** The grid renders from
 *    the document list alone; a tile without a sketch draws a neutral
 *    placeholder and swaps it in when it arrives. A screenful of tiles must
 *    never be lost to a preview -- which is also why `null` is a first-class
 *    answer here rather than an error: the server returns it for anything it
 *    cannot compute or the caller may not see.
 *
 * The batch is coalesced across a frame or two and capped at the route's 50,
 * so scrolling a long grid issues a handful of requests rather than one per
 * tile.
 */

import { api } from './api'
import type { Sketch } from './components/doclist-types'

/** The route's cap. Asking for more is a 400, by design. */
const MAX_BATCH = 50
/** Long enough for a screenful of tiles to arrive, short enough to feel absent. */
const COALESCE_MS = 60

class SketchStore {
  /** `undefined` not asked yet, `null` asked and there is nothing to draw. */
  byId = $state<Record<string, Sketch | null>>({})

  #queued = new Set<string>()
  #asked = new Set<string>()
  #timer: ReturnType<typeof setTimeout> | null = null
  #observer: IntersectionObserver | null = null
  #watched = new WeakMap<Element, string>()

  /** Ask for this id, once. Idempotent and cheap to call from a render. */
  want(id: string): void {
    if (this.#asked.has(id) || this.#queued.has(id)) return
    this.#queued.add(id)
    if (this.#timer) return
    this.#timer = setTimeout(() => {
      this.#timer = null
      void this.#flush()
    }, COALESCE_MS)
  }

  async #flush(): Promise<void> {
    const batch = [...this.#queued].slice(0, MAX_BATCH)
    if (batch.length === 0) return
    for (const id of batch) {
      this.#queued.delete(id)
      this.#asked.add(id)
    }
    try {
      const res = await api<{ sketches: Record<string, Sketch | null> }>('/v1/docs/sketches', {
        method: 'POST',
        body: JSON.stringify({ ids: batch }),
      })
      const got = res.sketches ?? {}
      for (const id of batch) this.byId[id] = got[id] ?? null
    } catch {
      // A preview is not worth a message. The tiles are already on screen with
      // their names and their metadata; they simply keep the placeholder.
      for (const id of batch) this.byId[id] = null
    }
    // Anything that arrived while this was in flight.
    if (this.#queued.size > 0 && !this.#timer) {
      this.#timer = setTimeout(() => {
        this.#timer = null
        void this.#flush()
      }, COALESCE_MS)
    }
  }

  /**
   * ONE observer for the whole grid, not one per tile: a hundred tiles with a
   * hundred observers is a hundred callbacks on every scroll frame.
   */
  #ensureObserver(): IntersectionObserver | null {
    if (this.#observer) return this.#observer
    if (typeof IntersectionObserver === 'undefined') return null
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const id = this.#watched.get(entry.target)
          if (id) this.want(id)
        }
      },
      // A screen's worth of margin, so a sketch is usually there by the time
      // the tile is scrolled to rather than arriving under the pointer.
      { rootMargin: '200px' },
    )
    return this.#observer
  }

  /** Watch a tile. Returns its own teardown, for the component's effect. */
  observe(el: Element, id: string): () => void {
    const observer = this.#ensureObserver()
    if (!observer) {
      // No IntersectionObserver (an old browser, a test): ask for everything
      // rather than showing placeholders forever. Degrading to "more requests"
      // is the right direction for a preview.
      this.want(id)
      return () => {}
    }
    this.#watched.set(el, id)
    observer.observe(el)
    return () => {
      this.#watched.delete(el)
      observer.unobserve(el)
    }
  }
}

export const sketches = new SketchStore()
